import { useState, useEffect } from 'react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { PlusCircle, MapPin, CheckCircle, Mail, Phone, Copy, ExternalLink, Trash2, Camera } from 'lucide-react';

export default function FranchiseBranches() {
    const toast = useToast();
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAppointModalOpen, setIsAppointModalOpen] = useState(false);
    const [appointForm, setAppointForm] = useState({ branchName: '', fullName: '', email: '', phone: '' });
    const [appointLoading, setAppointLoading] = useState(false);
    const [appointedBranchUrl, setAppointedBranchUrl] = useState('');
    const [appointedSetupLink, setAppointedSetupLink] = useState('');
    const [deletingId, setDeletingId] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [branchToDelete, setBranchToDelete] = useState(null);
    const [uploadingBranchId, setUploadingBranchId] = useState(null);

    const handleImageUpload = async (branchId, file) => {
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            toast.error('File size exceeds the 2 MB limit');
            return;
        }

        const formData = new FormData();
        formData.append('image', file);
        formData.append('type', 'items');
        formData.append('restaurantId', branchId);

        setUploadingBranchId(branchId);
        toast.info('Uploading branch image...');
        try {
            const uploadRes = await api.post('/api/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const imageUrl = uploadRes.data.secureUrl;

            await api.put(`/api/franchises/branches/${branchId}`, { imageUrl });
            toast.success('Branch image updated successfully!');
            fetchBranches();
        } catch (err) {
            console.error('Image upload failed:', err);
            toast.error(err.response?.data?.message || 'Failed to upload image');
        } finally {
            setUploadingBranchId(null);
        }
    };

    const fetchBranches = async () => {
        try {
            setLoading(true);
            const res = await api.get('/api/franchises/branches');
            setBranches(res.data);
        } catch (err) {
            console.error('Failed to fetch branches:', err);
            toast.error('Failed to load branches');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBranches();
    }, []);

    const handleDeleteBranch = (branchId, branchName) => {
        setBranchToDelete({ id: branchId, name: branchName });
        setDeleteModalOpen(true);
    };

    const confirmDeleteBranch = async () => {
        if (!branchToDelete) return;
        const { id, name } = branchToDelete;
        setDeletingId(id);
        setDeleteModalOpen(false);
        try {
            await api.delete(`/api/franchises/branches/${id}`);
            toast.success(`Branch "${name}" deleted successfully`);
            fetchBranches();
        } catch (err) {
            console.error('Delete branch error:', err);
            toast.error(err.response?.data?.message || 'Failed to delete branch');
        } finally {
            setDeletingId(null);
            setBranchToDelete(null);
        }
    };

    const handleAppointSubmit = async (e) => {
        e.preventDefault();
        setAppointLoading(true);
        try {
            const res = await api.post('/api/franchises/appoint', appointForm);
            const branchUrl = `${window.location.origin}/branch/${res.data.branch.slug}`;
            setAppointedBranchUrl(branchUrl);
            setAppointedSetupLink(res.data.setupLink || '');
            toast.success('Restaurant admin appointed successfully!');
            setAppointForm({ branchName: '', fullName: '', email: '', phone: '' });
            fetchBranches();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to appoint branch admin');
        } finally {
            setAppointLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-8 pb-20">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight">Branch Directory</h1>
                    <p className="text-slate-500 text-sm mt-1">Manage and appoint administrators for your brand's branch locations.</p>
                </div>
                <button
                    onClick={() => { setAppointedBranchUrl(''); setAppointedSetupLink(''); setIsAppointModalOpen(true); }}
                    className="btn-primary inline-flex items-center space-x-2 shadow-lg shadow-primary-500/20"
                >
                    <PlusCircle className="w-4 h-4" />
                    <span>Appoint Branch Admin</span>
                </button>
            </div>

            <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-100">
                        <thead className="bg-slate-50/40">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">Branch Details</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">Branch URL</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">Appointed Admin</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">Subscription Status</th>
                                <th className="px-6 py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">Contact</th>
                                <th className="px-6 py-4 text-right text-xs font-bold text-slate-400 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-100">
                            {branches.map(branch => (
                                <tr key={branch.id} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-3">
                                            <div className="relative group w-12 h-12 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shadow-inner">
                                                {uploadingBranchId === branch.id ? (
                                                    <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                                                ) : branch.image_url ? (
                                                    <img src={branch.image_url} alt="" className="w-full h-full object-cover" />
                                                ) : (
                                                    <span className="text-slate-400 text-lg">🏪</span>
                                                )}
                                                
                                                <label className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center text-[9px] text-white font-black transition-opacity cursor-pointer gap-0.5">
                                                    <Camera className="w-3.5 h-3.5" />
                                                    <span>EDIT</span>
                                                    <input 
                                                        type="file" 
                                                        accept="image/*" 
                                                        className="hidden" 
                                                        disabled={uploadingBranchId === branch.id}
                                                        onChange={(e) => handleImageUpload(branch.id, e.target.files[0])}
                                                    />
                                                </label>
                                            </div>
                                            <div>
                                                <div className="font-bold text-slate-900">{branch.name}</div>
                                                <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                                                    <MapPin className="w-3.5 h-3.5" /> {branch.city || 'Not configured'}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        {(() => {
                                            const url = `${window.location.origin}/branch/${branch.slug}`;
                                            return (
                                                <div className="flex items-center gap-2 max-w-xs">
                                                    <span className="text-xs text-slate-500 font-mono truncate" title={url}>{url}</span>
                                                    <button
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(url);
                                                            toast.success('Branch URL copied!');
                                                        }}
                                                        className="flex-shrink-0 p-1.5 rounded-lg bg-slate-100 hover:bg-primary-50 hover:text-primary-600 text-slate-500 transition-colors"
                                                        title="Copy URL"
                                                    >
                                                        <Copy className="w-3 h-3" />
                                                    </button>
                                                    <a
                                                        href={url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="flex-shrink-0 p-1.5 rounded-lg bg-slate-100 hover:bg-blue-50 hover:text-blue-600 text-slate-500 transition-colors"
                                                        title="Open Branch"
                                                    >
                                                        <ExternalLink className="w-3 h-3" />
                                                    </a>
                                                </div>
                                            );
                                        })()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-left">
                                        <div className="text-sm font-semibold text-slate-700">{branch.admin_name || 'Admin Pending'}</div>
                                        {!branch.admin_verified && branch.invitation_token ? (
                                            <div className="mt-1 flex items-center gap-1.5">
                                                <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">Pending Setup</span>
                                                <button
                                                    onClick={() => {
                                                        const activationLink = `${window.location.origin}/set-password?email=${encodeURIComponent(branch.admin_email)}&token=${branch.invitation_token}&restaurantId=${branch.id}`;
                                                        navigator.clipboard.writeText(activationLink);
                                                        toast.success('Activation link copied!');
                                                    }}
                                                    className="inline-flex items-center gap-1 text-[10px] text-indigo-600 hover:text-indigo-800 font-bold hover:underline"
                                                    title="Copy Activation Link"
                                                >
                                                    <Copy className="w-2.5 h-2.5" />
                                                    <span>Copy Link</span>
                                                </button>
                                            </div>
                                        ) : branch.admin_verified ? (
                                            <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200 mt-1 inline-block">Active</span>
                                        ) : null}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                            branch.subscription_status === 'active' || branch.subscription_status === 'trialing'
                                                ? 'bg-emerald-100 text-emerald-800'
                                                : branch.subscription_status === 'pending_card'
                                                ? 'bg-amber-100 text-amber-800'
                                                : 'bg-rose-100 text-rose-800'
                                        }`}>
                                            {branch.subscription_status === 'pending_card' ? 'Pending Card Mandate' : (branch.subscription_status || 'inactive')}
                                        </span>
                                        {branch.plan_name && (
                                            <span className="text-slate-400 text-xs ml-2 font-mono">({branch.plan_name})</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-xs text-slate-500 flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {branch.admin_email || branch.contact_email}</div>
                                        {branch.contact_phone && (
                                            <div className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {branch.contact_phone}</div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                        <button
                                            onClick={() => handleDeleteBranch(branch.id, branch.name)}
                                            disabled={deletingId === branch.id}
                                            className="p-2 rounded-xl text-rose-500 hover:bg-rose-50 hover:text-rose-700 transition-colors disabled:opacity-50"
                                            title="Delete Branch Restaurant"
                                        >
                                            {deletingId === branch.id ? (
                                                <div className="w-4 h-4 border-2 border-rose-500 border-t-transparent rounded-full animate-spin"></div>
                                            ) : (
                                                <Trash2 className="w-4 h-4" />
                                            )}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {branches.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-slate-400 italic">
                                        No branches appointed yet. Click "Appoint Branch Admin" to add your first restaurant location.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Appoint Modal */}
            {isAppointModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
                    <div className="bg-white rounded-3xl max-w-lg w-full p-8 shadow-2xl border border-slate-100 relative animate-scale-up">
                        {appointedBranchUrl ? (
                            <div className="space-y-6 text-center">
                                <div className="w-16 h-16 bg-green-50 text-green-600 rounded-2xl flex items-center justify-center mx-auto">
                                    <CheckCircle className="w-8 h-8" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-black text-slate-900">Branch & Admin Created!</h3>
                                    <p className="text-slate-500 text-sm mt-2 leading-relaxed">
                                        An activation email has been sent to the admin (valid for 7 days). They must use the link in their email to configure their password and authorize their payment card before they can log in.
                                    </p>
                                </div>
                                <div className="space-y-2 text-left bg-slate-50 p-4 border border-slate-100 rounded-2xl">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Branch Storefront URL (Once activated)</span>
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="text-xs text-slate-600 select-all break-all font-mono">{appointedBranchUrl}</span>
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(appointedBranchUrl);
                                                toast.success('Storefront URL copied!');
                                            }}
                                            className="bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-xs px-3 py-1.5 rounded-lg font-bold transition-colors flex-shrink-0"
                                        >
                                            Copy
                                        </button>
                                    </div>
                                </div>
                                {appointedSetupLink && (
                                    <div className="space-y-2 text-left bg-slate-50 p-4 border border-slate-100 rounded-2xl">
                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Activation Link (Set Password & Card Mandate)</span>
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="text-xs text-slate-600 select-all break-all font-mono">{appointedSetupLink}</span>
                                            <button
                                                onClick={() => {
                                                    navigator.clipboard.writeText(appointedSetupLink);
                                                    toast.success('Activation Link copied!');
                                                }}
                                                className="bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-xs px-3 py-1.5 rounded-lg font-bold transition-colors flex-shrink-0"
                                            >
                                                Copy
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <button
                                    onClick={() => {
                                        setAppointedBranchUrl('');
                                        setAppointedSetupLink('');
                                        setIsAppointModalOpen(false);
                                    }}
                                    className="w-full bg-slate-950 hover:bg-slate-900 text-white py-3.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-colors"
                                >
                                    Close Window
                                </button>
                            </div>
                        ) : (
                            <>
                                <h3 className="text-2xl font-black text-slate-900 mb-2">Appoint Restaurant Branch Admin</h3>
                                <p className="text-slate-500 text-sm mb-6">Initialize a new franchise branch and appoint an administrator. They will receive an invitation email to set their login credentials.</p>

                                <form onSubmit={handleAppointSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Branch Name (e.g. Adyar, T-Nagar)</label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="Enter branch name"
                                            value={appointForm.branchName}
                                            onChange={(e) => setAppointForm({ ...appointForm, branchName: e.target.value })}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Admin Full Name</label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="Enter admin full name"
                                            value={appointForm.fullName}
                                            onChange={(e) => setAppointForm({ ...appointForm, fullName: e.target.value })}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Admin Email Address</label>
                                        <input
                                            type="email"
                                            required
                                            placeholder="admin@franchisebranch.com"
                                            value={appointForm.email}
                                            onChange={(e) => setAppointForm({ ...appointForm, email: e.target.value })}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Admin Contact Phone</label>
                                        <input
                                            type="tel"
                                            placeholder="+91 98765 43210"
                                            value={appointForm.phone}
                                            onChange={(e) => setAppointForm({ ...appointForm, phone: e.target.value })}
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                                        />
                                    </div>

                                    <div className="flex gap-4 pt-4 border-t border-slate-100">
                                        <button
                                            type="button"
                                            onClick={() => setIsAppointModalOpen(false)}
                                            className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-3.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={appointLoading}
                                            className="flex-1 btn-primary py-3.5 text-xs font-bold uppercase tracking-wider flex items-center justify-center space-x-2"
                                        >
                                            {appointLoading ? <span>Sending...</span> : <span>Appoint Admin</span>}
                                        </button>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteModalOpen && branchToDelete && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
                    <div className="bg-white rounded-3xl max-w-md w-full p-8 shadow-2xl border border-slate-100 relative animate-scale-up space-y-6 text-center">
                        <div className="w-16 h-16 bg-rose-50 text-rose-600 rounded-2xl flex items-center justify-center mx-auto">
                            <Trash2 className="w-8 h-8" />
                        </div>
                        <div>
                            <h3 className="text-2xl font-black text-slate-900">Delete Branch?</h3>
                            <p className="text-slate-500 text-sm mt-2 leading-relaxed">
                                Are you sure you want to permanently delete <strong className="text-slate-800">"{branchToDelete.name}"</strong>? 
                                <br />
                                This action is irreversible and will permanently delete all menu items, orders, and staff accounts.
                            </p>
                        </div>
                        <div className="flex gap-4 pt-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setDeleteModalOpen(false);
                                    setBranchToDelete(null);
                                }}
                                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-3.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmDeleteBranch}
                                className="flex-1 bg-rose-600 hover:bg-rose-700 text-white py-3.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-colors shadow-lg shadow-rose-200"
                            >
                                Delete Branch
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
